import { FunctionComponent } from "react";
import classNames from "classnames";
import { StarIcon } from "./icons";

type RatingComponentProps = {
  score: number | null;
  /** Omitted for a guest: the rating is then shown, not offered. */
  onChange?: (rating: number | null) => void;
};

const RATINGS = [1, 2, 3, 4, 5];

/*
 * One cell per star, the same size whether it is pressable or not.
 *
 * Narrower than it is tall: five stars have to read as one control rather than five
 * ornaments, and at 24px square the gaps were as wide as the stars themselves. 20px
 * keeps a usable target while closing the row up; the height stays 24 so the touch
 * area does not shrink with it.
 *
 * Sharing the geometry with the read-only branch is the other half. Those used to be
 * bare 14px icons with no cell at all, so a guest saw a tight row and an editor saw a
 * sparse one, and the list reflowed on sign-in.
 */
const CELL = "flex h-6 w-5 shrink-0 items-center justify-center";

export const RatingComponent: FunctionComponent<RatingComponentProps> = ({
  score,
  onChange,
}) => {
  const readOnly = !onChange;

  return (
    <div
      className={classNames("flex items-center", {
        "text-ink-faint": score === null,
        "text-amber-500": score !== null,
      })}
      role={readOnly ? "img" : "group"}
      aria-label={
        readOnly
          ? score === null
            ? "Not rated"
            : `Rated ${score} out of 5`
          : "Rating"
      }
    >
      {RATINGS.map((rating) => {
        const filled = score !== null && rating <= score;
        const star = <StarIcon filled={filled} className="size-4" />;

        if (readOnly) {
          return (
            <span key={rating} className={CELL}>
              {star}
            </span>
          );
        }

        return (
          <button
            key={rating}
            type="button"
            aria-label={rating === score ? `Clear rating` : `Rate ${rating} of 5`}
            aria-pressed={filled}
            onClick={() => onChange(rating === score ? null : rating)}
            className={classNames(
              CELL,
              "cursor-pointer rounded transition-colors hover:text-amber-500",
              "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
            )}
          >
            {star}
          </button>
        );
      })}
    </div>
  );
};
